import { ProductRating } from '@eliadmin/core/src/components/product/detail/product-rating';
import { AddReviewDialog } from '@eliadmin/core/src/components/product/dialog/add-review-dialog';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { ProductTabContainer } from './product-tab-container';
import type {
  ProductDetailData,
  ProductReview,
} from '@eliadmin/core/src/api/products/types/product-types';
import { ProductReviewCard } from '../product-review-card';
import { FaStar } from 'react-icons/fa';
import { SignInDialog } from "@eliadmin/core/src/components/dialog/lazy-dialogs";
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';

interface ProductTabReviewsProps {
  product: ProductDetailData;
  reviews: ProductReview[];
  isLoading: boolean;
  title: string;
}

export function ProductTabReviews({ product, reviews, title, isLoading }: ProductTabReviewsProps) {
  const { t } = useTranslations();

  const getReviewCountText = (count: number) => {
    if (count === 1) return `${count} recenzia`;
    if (count >= 2 && count <= 4) return `${count} recenzie`;
    return `${count} recenzií`;
  };

  return (
    <ProductTabContainer title={title}>
      <div className="flex w-full flex-col-reverse items-start justify-start gap-6 sm:gap-8 md:flex-row md:justify-between lg:gap-12">
        {isLoading ? (
          <Spinner />
        ) : (
          <div className="flex w-full max-w-[795px] flex-col items-start justify-start gap-4">
            {reviews.length > 0 ? (
              reviews.map(review => (
                <ProductReviewCard key={`${review.name}-${review.surname}`} review={review} />
              ))
            ) : (
              <p className="w-full text-sm sm:text-base">{t('text2359')}</p>
            )}
          </div>
        )}
        <ReviewSummaryCard reviews={reviews} product={product} />
      </div>
    </ProductTabContainer>
  );
}

function ReviewSummaryCard({
  reviews,
  product,
}: {
  reviews: ProductReview[];
  product: ProductDetailData;
}) {
  const { t } = useTranslations();
  const me = useCustomerStore(s => s.me);

  const handleSignInClick = () => {
    openDialog({
      id: SignInDialog.id,
      content: <SignInDialog />,
    });
  };

  const openDialog = useDialogStore(s => s.open);

  const handleCreateReviewClick = () => {
    openDialog({
      id: AddReviewDialog.id,
      content: <AddReviewDialog slug={product.slug} product={product} />,
    });
  };

  const averageRating = reviews?.length
    ? reviews.reduce((sum, review) => sum + Number(review.rating), 0) / reviews.length
    : 0;
  const hasRating = Number.isFinite(averageRating) && averageRating >= 1 && averageRating <= 5;
  const ratingPercentage = Math.round((averageRating / 5) * 100);

  return (
    <div className="flex w-full flex-col items-start justify-start gap-4 rounded-[20px] bg-[#FAFAFA] p-4 sm:min-w-[397px] sm:gap-5 sm:p-5 md:max-w-[397px] lg:gap-6 lg:p-6">
      <div className="flex w-full items-center justify-start gap-4">
        <strong className="whitespace-nowrap text-3xl font-medium tracking-tight lg:text-4xl">
          {hasRating ? `${ratingPercentage}%` : '—'}
        </strong>
        <div className="flex flex-col items-start justify-start gap-1">
          <div className="flex w-full items-center justify-start gap-2">
            <ProductRating
              rating={hasRating ? averageRating : 0}
              ratingCount={reviews.length}
              hideTextRating
              className={{
                starDisabled: 'fill-white stroke-[#EDB000] stroke-[1px]',
                starIcon: 'fill-[#EDB000] stroke-[#EDB000] stroke-[1px]',
              }}
            />
            {hasRating && (
              <p className="text-sm font-normal text-foreground">
                <span className="font-medium">
                  {averageRating.toFixed(1)}
                </span>
                /5
              </p>
            )}
          </div>
          <p className="text-sm text-[#636363] lg:text-base">
            {reviews.length === 0 ? `0 ${t('text2360')}` : `${reviews.length} recenzií`}
          </p>
        </div>
      </div>
      <div className="h-[1px] w-full flex-grow bg-black/5" />
      <div className="flex w-full flex-col items-start justify-start gap-4">
        {[5, 4, 3, 2, 1].map(starRating => {
          const ratingCount = reviews.filter(review => review.rating === starRating).length;
          const barWidth = ratingCount > 0 ? Math.min(ratingCount * 20, 100) : 1;

          return (
            <div key={starRating} className="flex w-full items-center justify-start gap-1">
              {[...Array(starRating)].map((_, index) => (
                <FaStar
                  size={18}
                  className="min-h-[18px] min-w-[18px] text-[#EDB000]"
                  key={index}
                />
              ))}
              {[...Array(5 - starRating)].map((_, index) => (
                <FaStar
                  size={18}
                  className="min-h-[18px] min-w-[18px] fill-white stroke-[#EDB000] text-[#EDB000]"
                  key={index}
                />
              ))}
              <div
                className="mx-1 h-[6px] min-w-2 rounded-full bg-[#00BA31]"
                style={{ width: `${barWidth}%` }}
              />
              <p className="whitespace-nowrap text-sm font-medium text-[#481132]">{ratingCount}x</p>
            </div>
          );
        })}
      </div>
      <Button
        onClick={me?.customer ? handleCreateReviewClick : handleSignInClick}
        className="w-full"
      >
        {t('text2306')}
      </Button>
    </div>
  );
}
